import React, { useEffect } from "react";

import TextArea from "antd/es/input/TextArea";
import { AutoComplete, Button, Card, Checkbox, Col, Divider, Empty, Flex, Form, FormInstance, Grid, Input, InputNumber, Popconfirm, Row, Select, Spin, Tooltip, Typography, } from "antd";
import { DeleteOutlined, PlusOutlined, QuestionCircleTwoTone } from "@ant-design/icons";
import { useForm, useWatch } from "antd/lib/form/Form";

import { useQuery } from "@tanstack/react-query";

import { TypeFichaDeSaude, TypeFichaEAlergia, TypeFichaEEnfermidade, TypeFichaEVacina } from "@/Models/Domain/Saude/FIcha/FichaDeSaude";
import { AlergiaService } from "@/Services/Domain/Saude/AlergiaService";
import { VacinaService } from "@/Services/Domain/Saude/VacinaService";
import { EnfermidadeService } from "@/Services/Domain/Saude/EnfermidadeService";
import { scrollarParaOElemento } from "@/Utils/Navegacao";

import dayjs from 'dayjs'
import InputDate from "@/Components/Base/Input/InputDate";

export const FichaDeSaude: React.FC<{
  ficha?: TypeFichaDeSaude;
  onFinish?: (v: TypeFichaDeSaude) => void;
  bloquearSubmissao?: boolean,
  form?:FormInstance<TypeFichaDeSaude>,
  criarBotaoSubmit?:boolean,
  txtSalvar?: string,
  txtAtualizar?: string,
}> = (props) => {
  const breakpoints = Grid.useBreakpoint();

  const txtSalvar = props.txtSalvar ?? "Cadastrar"
  const txtAtualizar = props.txtAtualizar ?? "Atualizar"

  const vacinasQuery = useQuery({
    queryKey: [`VacinaService.listar`, { ativo: true }],
    queryFn: () => VacinaService.listar({ ativo: true }),
    enabled: false,
  });

  const alergiasQuery = useQuery({
    queryKey: [`AlergiaService.listar`, { ativo: true }],
    queryFn: () => AlergiaService.listar({ ativo: true }),
    enabled: false,
  });

  const enfermidadesQuery = useQuery({
    queryKey: [`EnfermidadeService.listar`, { ativo: true }],
    queryFn: () => EnfermidadeService.listar({ ativo: true }),
    enabled: false,
  });

  const [newForm] = useForm<TypeFichaDeSaude>();
  const form = props.form ?? newForm;

  const valoresDoFormulario = useWatch([], form);

  useEffect(() => {
    if(props.ficha == undefined){
      form.resetFields();
    } else {
      const patch: TypeFichaDeSaude = {
         ...props.ficha,
         vacinas: props.ficha.vacinas?.map((v) => ({...v, dataDaUltimaAplicacao: v.dataDaUltimaAplicacao? dayjs(v.dataDaUltimaAplicacao) : undefined}))
      };

      form.setFieldsValue(patch);
    }

    /* Novas vacinas cadastradas que não constem na ficha são adicionadas ao formulário. Vacinas pré-existentes na ficha se mantém */
    /* Isso é necessário para que caso uma vacina seja desativada pelos administradores, ela não continue aparecendo na atualização de uma ficha de saúde */

    vacinasQuery.refetch().then((response) => {
      const vacinasCadastradas = response.data?.data ?? [];
      const vacinasDeclaradas = props.ficha?.vacinas ?? [];

      const patch: TypeFichaDeSaude["vacinas"] = vacinasCadastradas?.map((v): TypeFichaEVacina => {
        const equivalente = vacinasDeclaradas.find((vd) => vd.vacina?.codigo != undefined && vd.vacina.codigo == v.codigo);
        return { ...equivalente, vacina: v };
      });

      patch.forEach((v) => v.dataDaUltimaAplicacao = v.dataDaUltimaAplicacao ? dayjs(v.dataDaUltimaAplicacao) : undefined)

      patch?.sort((a, b) => (a.vacina?.codigo ?? 0) - (b.vacina?.codigo ?? 0));
      form.setFieldValue("vacinas", patch);
    });

    /* O mesmo se repete com as alergias */
    alergiasQuery.refetch().then((response) => {
      const alergiasCadastradas = response.data?.data ?? [];
      const alergiasDeclaradas = props.ficha?.alergias ?? [];

      const patch: TypeFichaDeSaude["alergias"] = alergiasCadastradas?.map((v): TypeFichaEAlergia => {
        const equivalente = alergiasDeclaradas.find((ad) => ad.alergia?.codigo != undefined && ad.alergia.codigo == v.codigo);
        return { ...equivalente, alergia: v };
      });

      patch?.sort((a, b) => (a.alergia?.codigo ?? 0) - (b.alergia?.codigo ?? 0));
      form.setFieldValue("alergias", patch);
    });

    /* E também com as orientações */
    enfermidadesQuery.refetch().then((response) => {
      const enfermidadesCadastradas = response.data?.data ?? [];
      const enfermidadesDeclaradas = props.ficha?.orientacoes ?? [];

      const patch: TypeFichaDeSaude["orientacoes"] = enfermidadesCadastradas?.map((v): TypeFichaEEnfermidade => {
        const equivalente = enfermidadesDeclaradas.find((ed) => ed.enfermidade?.codigo != undefined && ed.enfermidade.codigo == v.codigo);
        return { ...equivalente, enfermidade: v };
      });

      patch?.sort((a, b) => (a.enfermidade?.codigo ?? 0) - (b.enfermidade?.codigo ?? 0));
      form.setFieldValue("orientacoes", patch);
    });
  }, [props.ficha]);

  const onFinish = (valores: TypeFichaDeSaude) => {
    const payload: TypeFichaDeSaude = {
      ...valores,
      orientacoes: valores.orientacoes?.filter((item) => item.procedimento != undefined && item.procedimento.trim().length > 0 ),
      alergias: valores.alergias?.map((item) => ({...item, alergico: item.alergico == true})),
    }

    props.onFinish?.(payload);
  }

  return (
    <Form<TypeFichaDeSaude> form={form} onFinish={onFinish} layout="vertical" autoComplete="off" scrollToFirstError={{ behavior: "instant" }}
    >
      <Flex vertical gap={8}>

        <Flex id="ficha-saude-contatos" vertical style={{ gap: "inherit" }} className="no-antd-form-item-margin">
          <Typography.Text style={{ textAlign: "center" }}>
            <Typography.Title style={{ marginTop: 0 }} level={4}>
              Contatos de emergência{" "}
              <Tooltip title="Indica pessoas a serem contactadas em casos de necessidade">
                <QuestionCircleTwoTone />
              </Tooltip>
            </Typography.Title>
          </Typography.Text>

          <Form.List name={"contatos" as keyof TypeFichaDeSaude}>
            {(fields, operations) => {
              return (
                <Flex vertical style={{ gap: 8 }}>
                  {fields.map((f) => {
                    return (
                      <Flex key={f.key} vertical style={{ gap: "inherit" }}>
                        <Row gutter={[8, 8]} key={f.key}>
                          <Form.Item {...f} hidden name={[f.name, "codigo"]}>
                            <Input hidden />
                          </Form.Item>

                          <Col xs={24} lg={5}>
                            <Form.Item {...f} label={"Nome"} name={[f.name, "nome"]} rules={[{ required: true, message: "Insira o nome" }]}>
                              <Input placeholder="Insira o nome" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} lg={4}>
                            <Form.Item {...f} label={"Relação"} name={[f.name, "relacao"]} rules={[{ required: true, message: "Insira a relação" }]}>
                              <AutoComplete
                                options={[
                                  { value: "Pai" },
                                  { value: "Mãe" },
                                  { value: "Irmão ou irmã" },
                                ]}
                                placeholder="Insira a relação"
                                filterOption={(inputValue, option) => option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
                              />
                            </Form.Item>
                          </Col>

                          <Col xs={24} lg={6}>
                            <Form.Item
                              {...f}
                              label={"Celular"}
                              name={[f.name, "celular"]}
                              validateTrigger={"onBlur"}
                              rules={[
                                { required: true, message: "Insira o celular" },
                                {
                                  transform(value) {
                                    return value.trim();
                                  },
                                },
                                { pattern: /^[\d\(\)\+]{8,}$/, message: "Insira um celular válido" },
                              ]}
                            >
                              <Input placeholder="Insira o celular" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} lg={9}>
                            <Form.Item {...f} label={"E-mail"} name={[f.name, "email"]}>
                              <Input placeholder="Insira o e-mail" />
                            </Form.Item>
                          </Col>

                          <Col xs={24}>
                            <Form.Item
                              {...f}
                              label={"Informações complementares"}
                              name={[f.name, "informacaoComplementar"]}
                              rules={[{ max: 400, message: "As informações complementares não podem ultrapassar 400 caracteres" }]}
                            >
                              <TextArea
                                rows={3}
                                style={{ resize: "none" }}
                                placeholder="Informe detalhes adicionais (se necessário)"
                                maxLength={400}
                                showCount
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row>
                          <Flex align="center" justify="center" style={{ width: "-webkit-fill-available" }}>
                            <Form.Item>
                              <Popconfirm
                                onConfirm={() => {
                                  operations.remove(f.name);
                                }}
                                title={<>Tem certeza que deseja remover este item?</>}
                              >
                                <Button danger type="primary" icon={<DeleteOutlined />}>
                                  Remover
                                </Button>
                              </Popconfirm>
                            </Form.Item>
                          </Flex>
                        </Row>
                      </Flex>
                    );
                  })}

                  <Row>
                    <Col>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => {
                          operations.add();
                        }}
                      >
                        Novo contato
                      </Button>
                    </Col>
                  </Row>
                </Flex>
              );
            }}
          </Form.List>
        </Flex>

        <Divider />

        <Flex id="ficha-saude-vacinas" vertical style={{ gap: "inherit" }}>
          <Typography.Text style={{ textAlign: "center" }}>
            <Typography.Title level={4} style={{ marginTop: 0 }}>
              Vacinas
            </Typography.Title>
          </Typography.Text>

          <Spin spinning={vacinasQuery.isPending}>
            <Form.List name="vacinas">
              {(fields) => {
                return (
                  <Row gutter={[12, 12]}>
                    {fields.map((f, idx) => {
                      const current = valoresDoFormulario?.vacinas?.at(idx);

                      return (
                        <Col key={f.key} xs={24} sm={12} lg={6}>
                          <Card
                            title={current?.vacina?.nome}
                            style={{ boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px" }}
                          >
                            <Row gutter={[8, 8]}>
                              <Col>
                                <Form.Item {...f} label="Recebida?" name={[f.name, "tomou"]} rules={[{ required: true, message: "O campo é obrigatório" }]}>
                                  <Select
                                    placeholder="Selecione"
                                    options={[
                                      { value: true, label: "Sim" },
                                      { value: false, label: "Não" },
                                    ]}
                                  />
                                </Form.Item>
                              </Col>

                              {current?.tomou == true && (
                                <React.Fragment>
                                  <Col>
                                    <Form.Item {...f} name={[f.name, "doses"]} label="Doses" initialValue={1}>
                                      <InputNumber min={1} max={10} />
                                    </Form.Item>
                                  </Col>

                                  <Col>
                                    <Form.Item {...f} name={[f.name, "dataDaUltimaAplicacao"]} label="Última aplicação" >
                                      <InputDate placeholder="Selecione" disabledDate={(date) => date.isAfter(new Date())} />
                                    </Form.Item>
                                  </Col>

                                  {/* <Col>
                                    <Form.Item {...f} name={[f.name, "especie"]} label="Espécie">
                                      <Input placeholder="e.g. Coronavac" />
                                    </Form.Item>
                                  </Col> */}

                                </React.Fragment>
                              )}
                            </Row>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                );
              }}
            </Form.List>
          </Spin>

          {valoresDoFormulario?.alergias?.length == 0 && <Empty description="Nenhuma vacina a ser informada foi encontrada" />}
        </Flex>

        <Divider />

        <Flex id="ficha-saude-orientacoes" vertical style={{ gap: "inherit" }}>
          <Typography.Text style={{ textAlign: "center" }}>
            <Typography.Title style={{ marginTop: 0 }} level={4}>
              Informações sobre a saúde
            </Typography.Title>

            <Typography.Title style={{ marginTop: 0 }} level={5}>
              Como deve ser medicado em caso de:
            </Typography.Title>
          </Typography.Text>

          <Spin spinning={enfermidadesQuery.isFetching}>
            <Form.List name={"orientacoes" as keyof TypeFichaDeSaude}>
              {(fields) => {
                return (
                  <Flex vertical style={{ width: "100%" }} gap={4}>
                    <Row gutter={[8, 8]} style={{ display: "flex", justifyContent: "space-between" }}>
                      {fields.map((f, idx) => {
                        const current = valoresDoFormulario?.orientacoes?.at(idx);
                        return (
                          <Col key={f.key} xs={24} sm={12}>
                              <Typography.Text strong>
                                  {current?.enfermidade?.nome}{" "}
                                  <Tooltip title={current?.enfermidade?.descricao ?? "Nenhuma descrição adicionada"}>
                                      <QuestionCircleTwoTone />
                                  </Tooltip>
                              </Typography.Text>

                              <Form.Item {...f} name={[f.name, "procedimento"]}>
                                  <TextArea style={{ scrollbarWidth: "thin" }} maxLength={1000} placeholder="Indique o nome do remédio e a posologia" />
                              </Form.Item>
                          </Col>
                        );
                      })}
                    </Row>
                  </Flex>
                );
              }}
            </Form.List>
          </Spin>

          {valoresDoFormulario?.alergias?.length == 0 && <Empty description="Nenhuma enfermidade a ser informada foi encontrada" />}
        </Flex>

        <Divider />

        <Flex id="ficha-saude-alergias" className="no-antd-form-item-margin" vertical style={{ gap: "inherit" }}>
          <Typography.Text style={{ textAlign: "center" }}>
            <Typography.Title style={{ marginTop: 0 }} level={4}>
              Alergias
            </Typography.Title>
          </Typography.Text>

          <Spin spinning={alergiasQuery.isFetching}>
            <Form.List name={"alergias" as keyof TypeFichaDeSaude}>
              {(fields) => {
                return (
                  <Row gutter={[8, 16]} style={{ width: "100%" }}>
                    {fields.map((f, idx) => {
                      const current = valoresDoFormulario?.alergias?.at(idx);
                      return (
                        <Col key={f.key} xs={24} sm={12} >
                          <Row gutter={[8, 8]} key={f.key}>
                            <Col xs={24} style={{ display: "flex", gap: 0 }}>
                              <Form.Item {...f} name={[f.name, "alergico"]} valuePropName="checked">
                                <Checkbox defaultChecked={false}>
                                  <Typography.Text strong>{current?.alergia?.nome} </Typography.Text>
                                </Checkbox>
                              </Form.Item>

                              <Form.Item style={{ marginLeft: "-4px" }}>
                                <Tooltip title={current?.alergia?.descricao ?? "Nenhuma descrição adicionada"}>
                                  <QuestionCircleTwoTone />
                                </Tooltip>
                              </Form.Item>
                            </Col>

                            {current?.alergico && (
                              <Col xs={24}>
                                <Form.Item {...f} name={[f.name, "orientacao"]}>
                                  <TextArea
                                    rows={3}
                                    style={{ resize: "none" }}
                                    placeholder="Informe detalhes adicionais (se necessário)"
                                    maxLength={400}
                                    showCount
                                  />
                                </Form.Item>
                              </Col>
                            )}
                          </Row>
                        </Col>
                      );
                    })}
                  </Row>
                );
              }}
            </Form.List>
          </Spin>

          {valoresDoFormulario?.alergias?.length == 0 && <Empty description="Nenhuma alergia a ser informada foi encontrada" />}
        </Flex>

        <Divider />

        <Flex id="ficha-saude-medicamentos" vertical style={{ gap: "inherit" }} className="no-antd-form-item-margin">
          <Typography.Text style={{ textAlign: "center" }}>
            <Typography.Title style={{ marginTop: 0 }} level={4}>
              Medicamentos
            </Typography.Title>

            <Typography.Title style={{ marginTop: 0 }} level={5}>
              O aluno está tomando algum medicamento? Se sim, liste-os abaixo
            </Typography.Title>
          </Typography.Text>

          <Form.List name={"medicamentos" as keyof TypeFichaDeSaude}>
            {(fields, operations) => {
              return (
                <Flex vertical style={{ gap: 16 }}>
                  {fields.map((f, idx, arr) => {
                    return (
                      <div key={f.key}>
                        {idx > 0 && arr.length > 1 && <Divider />}
                        <Row gutter={[8, 8]}>
                          <Form.Item {...f} hidden name={[f.name, "codigo"]}>
                            <Input hidden />
                          </Form.Item>

                          <Col xs={24} sm={12} lg={10}>
                            <Form.Item {...f} label={"Motivo"} name={[f.name, "motivo"]} rules={[{ required: true, message: "Insira o motivo" }]}>
                              <Input placeholder="Insira o motivo" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={12} lg={7}>
                            <Form.Item
                              {...f}
                              label={"Medicamento"}
                              name={[f.name, "nome"]}
                              rules={[{ required: true, message: "O nome do medicamento é obrigatório" }]}
                            >
                              <Input placeholder="Insira o medicamento" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={12} lg={7}>
                            <Form.Item
                              {...f}
                              label={"Princípio ativo"}
                              name={[f.name, "principioAtivo"]}
                              rules={[{ required: true, message: "Insira o princípio ativo" }]}
                            >
                              <Input placeholder="Insira o princípio ativo" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={12} lg={5}>
                            <Form.Item {...f} label={"Dosagem"} name={[f.name, "dosagem"]} rules={[{ required: true, message: "A dosagem é obrigatória" }]}>
                              <Input placeholder="Insira a dosagem" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={12} lg={6}>
                            <Form.Item
                              {...f}
                              label={"Intervalo em horas"}
                              name={[f.name, "intervaloEmHoras"]}
                              rules={[
                                {
                                  required: true,
                                  max : 50,
                                  min : 1
                                }
                              ]}
                            >
                              <Input placeholder="12h" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} sm={12} lg={2} style={breakpoints.sm ? undefined : { display: "flex", justifyContent: "center" }}>
                            <Form.Item label={breakpoints.sm ? <></> : undefined}>
                              <Popconfirm
                                title={<>Tem certeza que deseja remover este item?</>}
                                onConfirm={() => {
                                  operations.remove(f.name);
                                }}
                              >
                                <Button danger type="primary" icon={<DeleteOutlined />}>
                                  Excluir
                                </Button>
                              </Popconfirm>
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    );
                  })}

                  <Row>
                    <Col>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => {
                          operations.add();
                        }}
                      >
                        Novo medicamento
                      </Button>
                    </Col>
                  </Row>
                </Flex>
              );
            }}
          </Form.List>
        </Flex>

        <Divider/>

        <Col xs={24}>
          <Form.Item<TypeFichaDeSaude> name={"informacaoComplementar"} label="Informações complementares">
            <TextArea maxLength={1000} rows={5} showCount placeholder="Informe detalhes a mais se necessário" style={{resize: "none"}}/>
          </Form.Item>
        </Col>

      </Flex>

      <Divider />

      {(props.onFinish && (props.criarBotaoSubmit ?? true === true)) && (
        <Flex style={{justifyContent: "center"}}>
          <Button
            type="primary"
            loading={props.bloquearSubmissao}
            disabled={props.bloquearSubmissao}
            onClick={() => {
              form
                .validateFields()
                .then(() => {
                  form.submit();
                })
                .catch((err) => {
                  /* 
                    Os formulários do AntDesign possuem a propriedade scrollToFirstError, que, em caso de erro de validação dos dados,
                    permite forçar a visualização sobre o primeiro campo invalidado. 

                    Entretanto, por alguns navegadores não implementarem a função nativa invocada pelo framework, pode não acabar funcionando corretamente.

                    Por isso o segmento a seguir.
                  */

                  const campoDoPrimeiroErro: string = err.errorFields[0].name[0];

                  const scroll: Partial<Record<keyof TypeFichaDeSaude, HTMLElement | null>> = {
                    contatos: document.getElementById("ficha-saude-contatos"),
                    vacinas: document.getElementById("ficha-saude-vacinas"),
                    orientacoes: document.getElementById("ficha-saude-orientacoes"),
                    alergias: document.getElementById("ficha-saude-alergias"),
                    medicamentos: document.getElementById("ficha-saude-medicamentos"),
                  };

                  const target = scroll[campoDoPrimeiroErro as keyof TypeFichaDeSaude];
                  if (target != undefined) {
                    scrollarParaOElemento(target);
                  }
                });
            }}
          >
            {props.ficha == undefined ? txtSalvar : txtAtualizar}
          </Button>
        </Flex>
      )}
    </Form>
  );
};
